import { z } from 'zod'
import type { AITool } from './types'
// import type { UnifiedExercise } from '../../types' // Currently unused
import { fuzzyScore, normalizeText } from '../fuzzy-search'

// Parameters schema - simplified for OpenAI compatibility
const parametersSchema = z.object({
  query: z.string()
    .min(1)
    .describe('Exercise name to search for'),
  limit: z.number()
    .min(1)
    .max(20)
    .describe('Maximum number of results to return (1-20)')
})

// Common exercise abbreviations
const ABBREVIATIONS: Record<string, string> = {
  'bb': 'barbell',
  'db': 'dumbbell',
  'ez': 'ez bar',
  'ohp': 'overhead press',
  'rdl': 'romanian deadlift',
  'sldl': 'stiff leg deadlift',
  'bp': 'bench press',
  'dl': 'deadlift',
  'sq': 'squat',
  'pr': 'press',
  'flys': 'flies',
  'tris': 'triceps',
  'bis': 'biceps',
  'delts': 'deltoids',
  'lats': 'latissimus'
}

// Expand abbreviations in query
function expandAbbreviations(query: string): string {
  let expanded = query.toLowerCase()
  
  // Replace known abbreviations
  Object.entries(ABBREVIATIONS).forEach(([abbr, full]) => {
    const regex = new RegExp(`\\b${abbr}\\b`, 'gi')
    expanded = expanded.replace(regex, full)
  })
  
  return expanded
}

interface ExerciseMatch {
  templateId: string
  name: string
  score: number
  primaryMover: string
  secondaryMovers: string[]
  source: 'static' | 'hevy'
  isCustom: boolean
  lastPerformed: string | null  // Date when last performed
  timesPerformed: number         // How many times performed
}

export const lookupExerciseTemplateID: AITool = {
  name: 'lookupExerciseTemplateID',
  description: 'DEPRECATED: Use searchExerciseLibrary or searchUserExercises instead.',
  parameters: parametersSchema,
  
  execute: async (args, context) => {
    const { query, limit = 5 } = args  // Default to 5 if not provided
    const { exerciseTemplates, workoutData, userSettings } = context
    
    // Check if exercise templates are available
    if (!exerciseTemplates || exerciseTemplates.length === 0) {
      const isApiConfigured = userSettings?.settings?.dataSource === 'api'
      return {
        summary: isApiConfigured 
          ? 'No exercise templates available. Ensure Hevy API key is configured.'
          : 'Exercise templates require API configuration.',
        query,
        matches: [],
        bestMatch: null,
        totalAvailable: 0
      }
    }
    
    // Expand abbreviations in query
    const expandedQuery = expandAbbreviations(query)
    
    // Also try reversing "Equipment Exercise" to "Exercise (Equipment)" format
    // e.g., "dumbbell bench press" → "bench press dumbbell"
    const words = expandedQuery.split(' ')
    let alternativeQuery = expandedQuery
    
    // Check if first word might be equipment
    const equipmentWords = ['barbell', 'dumbbell', 'cable', 'machine', 'smith', 'ez', 'kettlebell', 'band', 'bodyweight']
    if (words.length > 1 && equipmentWords.includes(words[0])) {
      // Move equipment word to end
      alternativeQuery = words.slice(1).concat(words[0]).join(' ')
    }
    
    // Build a map of exercise performance history
    const exerciseHistory = new Map<string, { lastDate: Date, count: number }>()
    
    if (workoutData.workouts) {
      // Process workouts from newest to oldest
      const sortedWorkouts = [...workoutData.workouts].sort((a, b) => 
        new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
      )
      
      sortedWorkouts.forEach(workout => {
        workout.exercises?.forEach(exercise => {
          const templateId = exercise.exercise_template_id
          if (!exerciseHistory.has(templateId)) {
            exerciseHistory.set(templateId, { 
              lastDate: new Date(workout.start_time), 
              count: 1 
            })
          } else {
            const existing = exerciseHistory.get(templateId)!
            existing.count++
          }
        })
      })
    }
    
    // Score all exercises
    const scoredExercises: ExerciseMatch[] = exerciseTemplates.map(exercise => {
      // Try scoring with original, expanded, and alternative queries
      const originalScore = fuzzyScore(query, exercise.name)
      const expandedScore = fuzzyScore(expandedQuery, exercise.name)
      const alternativeScore = fuzzyScore(alternativeQuery, exercise.name)
      const bestScore = Math.max(originalScore, expandedScore, alternativeScore)
      
      // Boost score for exact word matches
      const normalizedQuery = normalizeText(query)
      const normalizedName = normalizeText(exercise.name)
      const queryWords = normalizedQuery.split(' ')
      
      let wordBonus = 0
      queryWords.forEach(word => {
        if (normalizedName.split(' ').includes(word)) {
          wordBonus += 10
        }
      })
      
      let finalScore = Math.min(100, bestScore + wordBonus)
      
      // Get workout history for this exercise
      const history = exerciseHistory.get(exercise.id)
      
      // Boost score if exercise was performed recently (within last 30 days)
      if (history) {
        const daysSinceLastPerformed = Math.floor(
          (new Date().getTime() - history.lastDate.getTime()) / (1000 * 60 * 60 * 24)
        )
        if (daysSinceLastPerformed <= 30) {
          // Boost by up to 20 points for very recent exercises
          finalScore += Math.max(0, 20 - (daysSinceLastPerformed * 0.5))
        }
      }
      
      return {
        templateId: exercise.id,
        name: exercise.name,
        score: finalScore,
        primaryMover: exercise.primaryMover,
        secondaryMovers: exercise.secondaryMovers,
        source: exercise.source,
        isCustom: exercise.isCustom,
        lastPerformed: history ? history.lastDate.toISOString().split('T')[0] : null,
        timesPerformed: history ? history.count : 0
      }
    })
    
    // Filter and sort matches
    const matches = scoredExercises
      .filter(match => match.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
    
    // Find best match
    const bestMatch = matches.length > 0 
      ? {
          templateId: matches[0].templateId,
          name: matches[0].name,
          score: matches[0].score
        }
      : null
    
    // Generate summary
    let summary: string
    if (matches.length === 0) {
      summary = `No exercises found matching "${query}".`
    } else if (matches[0].score === 100) {
      summary = `Found exact match: ${matches[0].name}`
    } else if (matches.length === 1) {
      summary = `Found 1 exercise matching "${query}": ${matches[0].name}`
    } else {
      summary = `Found ${matches.length} exercises matching "${query}". Best match: ${matches[0].name} (${matches[0].score}% confidence)`
    }
    
    return {
      summary,
      query,
      matches: matches.map(m => ({
        templateId: m.templateId,
        name: m.name,
        score: m.score,
        primaryMover: m.primaryMover,
        lastPerformed: m.lastPerformed,
        timesPerformed: m.timesPerformed,
        recencyNote: m.lastPerformed ? 
          `Last performed: ${m.lastPerformed} (${m.timesPerformed} times total)` : 
          'Never performed'
      })),
      bestMatch: bestMatch,
      totalAvailable: exerciseTemplates.length
    }
  }
}